// BusinessLegalPage — the contractor's own Privacy Policy and Text Message Terms,
// at /business/:companyId/privacy and /business/:companyId/terms (#6069).
//
// These are the URLs a contractor submits with their SMS registration, so the
// same rule as BusinessPage.js applies: the page must read as THE BUSINESS'S
// page, not ours (Twilio 30506). The wording lives in
// constants/businessLegalTemplate.js and nowhere else.
//
// Reads exactly what BusinessPage reads — one row from get_public_business_page,
// which returns only the public fields and only when the contractor has switched
// the page on. A company that has not opted in gets "Page not found" here too.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import {
  LEGAL_LAST_UPDATED,
  privacySections,
  termsSections
} from '../constants/businessLegalTemplate';

export default function BusinessLegalPage({ kind }) {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_public_business_page', {
          p_company_id: companyId
        });
        if (cancelled) return;
        if (error) throw error;
        setCompany(Array.isArray(data) ? data[0] ?? null : data ?? null);
      } catch (err) {
        console.error('BusinessLegalPage load failed:', err);
        if (!cancelled) setCompany(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-900">Page not found</h1>
          <p className="mt-2 text-sm text-slate-500">
            This business page isn't available.
          </p>
        </div>
      </div>
    );
  }

  // Same name rules as BusinessPage: trading name is the headline, legal name
  // is the entity behind it.
  const legal = company.legal_name || company.name;
  const name = company.dba_name || company.name;
  const accent = company.theme_color || '#1e293b';
  const address = [
    company.address_line1,
    company.address_line2,
    [company.city, company.state_province, company.postal_code].filter(Boolean).join(', ')
  ].filter(Boolean).join(', ');

  const b = {
    name,
    legal,
    email: company.email || '',
    phone: company.phone || '',
    website: company.website || '',
    address
  };

  const isPrivacy = kind === 'privacy';
  const title = isPrivacy ? 'Privacy Policy' : 'Text Message Terms';
  const sections = isPrivacy ? privacySections(b) : termsSections(b);
  const base = `/business/${companyId}`;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <a href={base} className="text-2xl font-bold tracking-tight hover:underline" style={{ color: accent }}>
            {name}
          </a>
          <h1 className="mt-4 text-xl font-semibold">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">Last updated: {LEGAL_LAST_UPDATED}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        {sections.map((section) => (
          <section key={section.heading}>
            <h2 className="text-base font-semibold text-slate-900">{section.heading}</h2>
            <div className="mt-2 space-y-2 text-sm text-slate-700 leading-relaxed">
              {section.body.map((para) => <p key={para}>{para}</p>)}
            </div>
          </section>
        ))}

        <section className="text-sm text-slate-500 space-x-4">
          <a href={base} className="hover:underline">{name}</a>
          {isPrivacy
            ? <a href={`${base}/terms`} className="hover:underline">Text Message Terms</a>
            : <a href={`${base}/privacy`} className="hover:underline">Privacy Policy</a>}
        </section>
      </main>
    </div>
  );
}
